import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { WHATSAPP_ACCESS, hasPermission } from "../lib/permissions";
import api, { errorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { ALERT_DELIVERY_KINDS, QUEUE_STATUS_LABELS, WEEKLY_DAYS, deliveryEqual, deliveryFromApiSettings, deliveryMissingGroup, weeklyScheduleEqual, weeklyScheduleFromApiSettings, whatsappStatusLabel } from "../lib/whatsapp";
import WeeklySummaryPreview from "../components/WeeklySummaryPreview";
import DeliveryJournal from "../components/DeliveryJournal";
import AlertDeliverySettings from "../components/AlertDeliverySettings";
import { useBottomBar } from "../hooks/useCopilotDock";
import { toast } from "sonner";
import { BarChart3, BookOpen, CheckSquare, Headset, Bell, MessageCircle, Sheet, Server, RefreshCw, Loader2, Sparkles, LifeBuoy, Trash2, Receipt, FileText, ShoppingCart, Workflow, Bot, SlidersHorizontal } from "lucide-react";
import { TRAINING_KEYS } from "../lib/training";
import BottomActionBar from "../components/BottomActionBar";

// Sections réellement fonctionnelles : Général/Pipeline/Prospects/Sécurité
// sont absents faute de consommateur backend. Pas de rubrique vide pour
// "remplir" le menu.
const SECTIONS = [
  { to: "general", label: "Général", icon: SlidersHorizontal, permission: "settings:view" },
  { to: "tasks", label: "Tâches", icon: CheckSquare, permission: "settings:view" },
  { to: "notifications", label: "Notifications", icon: Bell, permission: "settings:view" },
  // WhatsApp/Google Sheets/Système ont chacun leur permission, jamais settings:view : voir les paramètres ne donne
  // accès ni au compte WhatsApp ni à l'état technique (délégables depuis le 15/10/2026, admin d'office).
  { to: "whatsapp", label: "WhatsApp", icon: MessageCircle, permission: WHATSAPP_ACCESS },
  { to: "google-sheet", label: "Google Sheets", icon: Sheet, permission: "page:google_sheet" },
  { to: "system", label: "Système", icon: Server, permission: "system:view" },
  // Jamora Assist: how many technicians may be in one assistance (only when the module is on).
  { to: "assist", label: "Assistance", icon: Headset, permission: "settings:view", feature: "assist" },
  // Tickets support (Ordre 08, D-16): response and resolution objectives per priority, only when the module is on.
  { to: "tickets", label: "Support", icon: LifeBuoy, permission: "settings:view", feature: "tickets" },
  // Automatisations (Ordre 25): the rules the CRM applies by itself, only when the module is on.
  { to: "automation", label: "Automatisations", icon: Workflow, permission: "automation:view", feature: "automation" },
  // Agents externes (Ordre 25-5): one's own read-only access tokens, only when the module is on.
  { to: "agents", label: "Agents externes", icon: Bot, permission: "automation:manage_agents", feature: "agent_access" },
  // Corbeille (Ordre 28): how long a deleted item can be restored.
  { to: "deletion", label: "Corbeille", icon: Trash2, permission: "settings:view" },
  // Revenus (Ordre 17, D-34): the payment delay of the installments, only when the module is on.
  { to: "billing", label: "Revenus", icon: Receipt, permission: "settings:view", feature: "billing" },
  // Documents et contrats (Ordre 18-3, D-41): when the obligations' alerts go out, only when the module is on.
  { to: "records", label: "Documents", icon: FileText, permission: "settings:view", feature: "records" },
  // Achats et actifs (Ordre 20-4): currencies, approval threshold and deadline alerts, only when the module is on.
  { to: "procurement", label: "Achats", icon: ShoppingCart, permission: "settings:view", feature: "procurement" },
  // Formations (Ordre 11): the catalogue of Jamora's features, read with a training key, changed with training:manage_catalog.
  { to: "training-catalog", label: "Fonctionnalités Jamora", icon: BookOpen, permission: TRAINING_KEYS, feature: "training" },
  // Copilote Jamora: administrator only (« assistant:manage » is admin_only: no role can hold it), and only when the module is on.
  { to: "assistant", label: "Copilote Jamora", icon: Sparkles, admin: true, feature: "assistant" },
  // Its consumption is delegable (« assistant:view_usage »): figures and costs, never the text of the exchanges.
  { to: "assistant-usage", label: "Consommation du Copilote", icon: BarChart3, permission: "assistant:view_usage", feature: "assistant" },
];

function visibleSections(user) {
  return SECTIONS.filter((s) => {
    if (s.feature && !user?.features?.[s.feature]) return false;
    return s.admin ? user?.role === "admin" : hasPermission(user, s.permission);
  });
}

// /settings opens on the first section this account may see (not always Tâches, which needs settings:view).
export function SettingsIndex() {
  const { user } = useAuth();
  const first = visibleSections(user)[0];
  return <Navigate to={first ? first.to : "/pipeline"} replace />;
}

export default function Settings() {
  const { user } = useAuth();
  const location = useLocation();
  const sections = useMemo(() => visibleSections(user), [user]);
  const current = sections.find((s) => location.pathname.endsWith(`/${s.to}`));

  if (sections.length === 0) {
    // settings:view gates the route already; this is a defensive fallback,
    // not the primary access control.
    return <Navigate to="/pipeline" replace />;
  }

  return (
    <div className="space-y-5" data-testid="settings-page">
      <div>
        <h1 className="font-display font-bold text-2xl text-slate-900">Paramètres</h1>
        <p className="text-sm text-slate-500">Gérez la configuration et les intégrations de Jamora.</p>
      </div>

      {/* Mobile : tabs horizontaux scrollables, jamais la sidebar verticale répliquée */}
      <nav className="lg:hidden -mx-4 px-4 overflow-x-auto">
        <div className="flex gap-2 pb-1 w-max">
          {sections.map((s) => (
            <NavLink
              key={s.to}
              to={s.to}
              data-testid={`settings-tab-${s.to}`}
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                  isActive ? "bg-jamora text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`
              }
            >
              <s.icon size={14} />
              {s.label}
            </NavLink>
          ))}
        </div>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-5 items-start">
        <nav className="hidden lg:block space-y-1 sticky top-6">
          {sections.map((s) => (
            <NavLink
              key={s.to}
              to={s.to}
              data-testid={`settings-nav-${s.to}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition ${
                  isActive
                    ? "bg-jamora/10 text-jamora font-medium"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <s.icon size={16} />
              {s.label}
            </NavLink>
          ))}
        </nav>

        <div className="min-w-0" key={current?.to}>
          <Outlet />
        </div>
      </div>
    </div>
  );
}

// --- Fil d'Ariane commun aux deux sections génériques ci-dessous ------------

export function SaveBar({ dirty, saving, canManage, saveDisabled = false, onSave, onRevert }) {
  useBottomBar(canManage && dirty);
  // `dirty` alone controls whether the bar appears (something changed);
  // `saveDisabled` (e.g. an invalid hours value) only disables the submit. An
  // in-progress edit must never hide the bar, or the user loses the "unsaved
  // changes" signal exactly when they need it most.
  if (!canManage || !dirty) return null;
  return (
    <BottomActionBar className="justify-end sm:justify-between">
      {/* On a phone the two buttons alone fill the bar: the sentence would push « Enregistrer » out of it. */}
      <span className="text-xs text-slate-500 hidden sm:inline-flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-jamora" /> Des modifications ont été apportées
      </span>
      <div className="flex gap-2">
        <Button variant="ghost" size="sm" onClick={onRevert} data-testid="btn-settings-revert">
          Annuler
        </Button>
        <Button
          size="sm"
          onClick={onSave}
          disabled={saving || saveDisabled}
          data-testid="btn-settings-save"
        >
          {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
          Enregistrer les modifications
        </Button>
      </div>
    </BottomActionBar>
  );
}

export function SectionHeader({ title, description }) {
  return (
    <div className="mb-4">
      <h3 className="font-display font-semibold text-lg text-slate-900">{title}</h3>
      {description && <p className="text-sm text-slate-500 mt-0.5">{description}</p>}
    </div>
  );
}

export function LoadError({ onRetry }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-8 text-center space-y-3">
      <p className="text-sm text-slate-500">Impossible de charger ces paramètres.</p>
      <Button variant="outline" size="sm" onClick={onRetry} data-testid="btn-settings-retry">
        Réessayer
      </Button>
    </div>
  );
}

// --- Tâches -------------------------------------------------------------

const HOURS_MIN = 1;
const HOURS_MAX = 720;

export function SettingsTasks() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "settings:manage");
  const [saved, setSaved] = useState(null);
  const [dueSoonEnabled, setDueSoonEnabled] = useState(true);
  const [dueSoonHours, setDueSoonHours] = useState("48");
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/settings/tasks");
      setSaved(data.settings);
      setDueSoonEnabled(!!data.settings.due_soon_enabled);
      setDueSoonHours(String(data.settings.due_soon_hours));
    } catch (e) {
      setLoadFailed(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hoursNumber = Number(dueSoonHours);
  const hoursValid = Number.isInteger(hoursNumber) && hoursNumber >= HOURS_MIN && hoursNumber <= HOURS_MAX;

  // Change detection is independent of validity: an invalid in-progress
  // edit is still a change the user needs to see (bar visible, save
  // disabled), not one that silently hides the bar.
  const dirty =
    saved != null &&
    (dueSoonEnabled !== !!saved.due_soon_enabled || String(dueSoonHours) !== String(saved.due_soon_hours));

  const revert = () => {
    setDueSoonEnabled(!!saved.due_soon_enabled);
    setDueSoonHours(String(saved.due_soon_hours));
  };

  const save = async () => {
    if (!hoursValid) return;
    setSaving(true);
    try {
      const { data } = await api.patch("/settings/tasks", {
        due_soon_enabled: dueSoonEnabled,
        due_soon_hours: hoursNumber,
      });
      setSaved(data.settings);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loadFailed) return <LoadError onRetry={load} />;
  if (saved == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  return (
    <div className="space-y-4" data-testid="settings-tasks">
      <SectionHeader title="Tâches" description="Rappels automatiques avant l'échéance d'une tâche." />

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-slate-900">Rappels d'échéance</div>
            <p className="text-xs text-slate-500 mt-0.5 max-w-md">
              Envoie une notification WhatsApp au responsable d'une tâche avant sa date d'échéance.
            </p>
          </div>
          <Switch
            checked={dueSoonEnabled}
            onCheckedChange={setDueSoonEnabled}
            disabled={!canManage}
            data-testid="switch-due-soon-enabled"
          />
        </div>

        <div className={`border-t border-slate-100 pt-4 ${!dueSoonEnabled ? "opacity-50" : ""}`}>
          <Label htmlFor="due-soon-hours" className="text-sm">Prévenir avant l'échéance</Label>
          <div className="flex items-center gap-2 mt-1.5 max-w-[160px]">
            <Input
              id="due-soon-hours"
              type="number"
              min={HOURS_MIN}
              max={HOURS_MAX}
              value={dueSoonHours}
              onChange={(e) => setDueSoonHours(e.target.value)}
              disabled={!canManage || !dueSoonEnabled}
              data-testid="input-due-soon-hours"
            />
            <span className="text-sm text-slate-500">heures</span>
          </div>
          {!hoursValid ? (
            <p className="text-xs text-red-600 mt-1.5">Entre {HOURS_MIN} et {HOURS_MAX} heures (30 jours maximum).</p>
          ) : (
            <p className="text-xs text-slate-400 mt-1.5">Entre {HOURS_MIN} et {HOURS_MAX} heures (30 jours maximum).</p>
          )}
        </div>
      </div>

      <SaveBar
        dirty={dirty}
        saveDisabled={!hoursValid}
        saving={saving}
        canManage={canManage}
        onSave={save}
        onRevert={revert}
      />
    </div>
  );
}

// --- Notifications --------------------------------------------------------

const EVENT_ROWS = [
  { key: "prospect_assigned", label: "Nouveau prospect attribué" },
  { key: "task_assigned", label: "Nouvelle tâche attribuée" },
  { key: "task_due_soon", label: "Échéance de tâche proche" },
  { key: "account_created", label: "Compte utilisateur créé" },
  { key: "password_reset_requested", label: "Réinitialisation demandée" },
  { key: "assist_request", label: "Nouvelle demande d'assistance" },
  { key: "equipment_issue", label: "Anomalie matérielle critique" },
  { key: "mission_report", label: "Compte rendu de mission à valider ou renvoyé" },
  { key: "procurement_alert", label: "Échéance d'un actif (garantie, renouvellement, retour de prêt)" },
  { key: "records_obligation", label: "Obligation qui arrive à échéance (documents et contrats)" },
  { key: "ticket", label: "Tickets support : attribué, critique ou venu de Jamora" },
  { key: "product_delivered", label: "Correctif livré pour un de vos tickets (retours produit)" },
  { key: "product_incident", label: "Incident majeur ou critique ouvert (produit)" },
  { key: "governance", label: "Décision soumise à votre arbitrage (gouvernance)" },
  { key: "claim_decision", label: "Note de frais approuvée, refusée ou renvoyée (à la personne qui l'a déclarée)" },
  { key: "claim_review", label: "Note de frais à approuver (aux approbateurs)" },
  { key: "mission_team", label: "Ajout à une mission, mission reportée, annulée ou interrompue" },
  { key: "task_changed", label: "Tâche confiée à quelqu'un d'autre ou annulée" },
  { key: "customer", label: "Compte client confié, ou client qui passe « à risque »" },
  { key: "training", label: "Formateur désigné sur une session de formation" },
  { key: "collaborator_deadline", label: "Habilitation qui expire, mandat légal à revoir" },
  { key: "governance_outcome", label: "Décision adoptée, différée ou annulée (à son auteur)" },
  { key: "weekly_summary", label: "Résumé hebdomadaire (coupe aussi celui du groupe)" },
];

function eventsFromApiSettings(settings) {
  const events = {};
  for (const row of EVENT_ROWS) events[row.key] = !!settings[`events.${row.key}`];
  return events;
}

const deliveriesFromApiSettings = (settings) =>
  Object.fromEntries(ALERT_DELIVERY_KINDS.map((kind) => [kind, deliveryFromApiSettings(settings, kind)]));
const deliveriesEqual = (a, b) => ALERT_DELIVERY_KINDS.every((kind) => deliveryEqual(a[kind], b[kind]));

function eventsEqual(a, b) {
  return EVENT_ROWS.every((row) => !!a[row.key] === !!b[row.key]);
}

export function SettingsNotifications() {
  const { user } = useAuth();
  const canManage = hasPermission(user, "settings:manage");
  const [saved, setSaved] = useState(null);
  const [whatsappEnabled, setWhatsappEnabled] = useState(true);
  const [events, setEvents] = useState({});
  const [deliveries, setDeliveries] = useState(deliveriesFromApiSettings({}));
  const [weeklySchedule, setWeeklySchedule] = useState(weeklyScheduleFromApiSettings({}));
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/settings/notifications");
      setSaved(data.settings);
      setWhatsappEnabled(!!data.settings["whatsapp.enabled"]);
      setEvents(eventsFromApiSettings(data.settings));
      setDeliveries(deliveriesFromApiSettings(data.settings));
      setWeeklySchedule(weeklyScheduleFromApiSettings(data.settings));
    } catch (e) {
      setLoadFailed(true);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const savedEvents = useMemo(() => (saved ? eventsFromApiSettings(saved) : {}), [saved]);
  const savedDeliveries = useMemo(() => deliveriesFromApiSettings(saved || {}), [saved]);
  const savedWeeklySchedule = useMemo(() => weeklyScheduleFromApiSettings(saved || {}), [saved]);
  const dirty =
    saved != null && (whatsappEnabled !== !!saved["whatsapp.enabled"] || !eventsEqual(events, savedEvents) || !deliveriesEqual(deliveries, savedDeliveries)
      || !weeklyScheduleEqual(weeklySchedule, savedWeeklySchedule));

  const revert = () => {
    setWhatsappEnabled(!!saved["whatsapp.enabled"]);
    setEvents(savedEvents);
    setDeliveries(savedDeliveries);
    setWeeklySchedule(savedWeeklySchedule);
  };

  const toggleEvent = (key) => {
    // Chaque Switch événement est désactivé tant que le canal global est OFF,
    // mais sa valeur n'est jamais réinitialisée : elle reste intacte en base et
    // se réapplique dès la réactivation du canal.
    setEvents((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { "whatsapp.enabled": whatsappEnabled };
      for (const row of EVENT_ROWS) payload[`events.${row.key}`] = !!events[row.key];
      for (const kind of ALERT_DELIVERY_KINDS) {
        payload[`${kind}.delivery`] = deliveries[kind].delivery;
        payload[`${kind}.group_jid`] = deliveries[kind].group_jid;
        payload[`${kind}.group_name`] = deliveries[kind].group_name;
      }
      payload["weekly.day"] = weeklySchedule.day;
      payload["weekly.hour"] = Number(weeklySchedule.hour);
      const { data } = await api.patch("/settings/notifications", payload);
      setSaved(data.settings);
      toast.success("Paramètres enregistrés");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  if (loadFailed) return <LoadError onRetry={load} />;
  if (saved == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  return (
    <div className="space-y-4" data-testid="settings-notifications">
      <SectionHeader title="Notifications" description="Alertes WhatsApp envoyées automatiquement par Jamora." />

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {/* Master toggle : visuellement plus fort que les événements qu'il gouverne */}
        <div className="flex items-start justify-between gap-4 p-5 bg-slate-50/60">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-jamora/10 text-jamora flex items-center justify-center flex-shrink-0">
              <MessageCircle size={16} />
            </div>
            <div>
              <div className="font-display font-semibold text-sm text-slate-900">Notifications WhatsApp</div>
              <p className="text-xs text-slate-500 mt-0.5 max-w-md">
                Active ou désactive l'envoi de notifications WhatsApp pour toute l'équipe.
              </p>
            </div>
          </div>
          <Switch
            checked={whatsappEnabled}
            onCheckedChange={setWhatsappEnabled}
            disabled={!canManage}
            data-testid="switch-whatsapp-enabled"
          />
        </div>

        {/* Événements : indentés/reliés visuellement au toggle maître ci-dessus */}
        <div className="p-5 pl-8 border-t border-slate-100">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-3">Événements</div>
          {!whatsappEnabled && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
              Réactivez les notifications WhatsApp pour utiliser ces événements.
            </p>
          )}
          <div className={`space-y-3 border-l-2 border-slate-100 pl-4 ${!whatsappEnabled ? "opacity-50" : ""}`}>
            {EVENT_ROWS.map((row) => (
              <div key={row.key} className="flex items-center justify-between gap-3">
                <span className="text-sm text-slate-600">{row.label}</span>
                <Switch
                  checked={!!events[row.key]}
                  onCheckedChange={() => toggleEvent(row.key)}
                  disabled={!canManage || !whatsappEnabled}
                  data-testid={`switch-event-${row.key}`}
                />
              </div>
            ))}
          </div>
        </div>
        <AlertDeliverySettings kind="assist" value={deliveries.assist} onChange={(value) => setDeliveries((d) => ({ ...d, assist: value }))}
          canManage={canManage} disabled={!whatsappEnabled}
          canListGroups={hasPermission(user, "whatsapp:manage")} canSeeStatus={hasPermission(user, WHATSAPP_ACCESS)} />
        {user?.features?.tickets && (
          // The last group message line is shown once (under the assistance block): it is the WhatsApp account's, not a kind's.
          <AlertDeliverySettings kind="tickets" value={deliveries.tickets} onChange={(value) => setDeliveries((d) => ({ ...d, tickets: value }))}
            canManage={canManage} disabled={!whatsappEnabled} canListGroups={hasPermission(user, "whatsapp:manage")} canSeeStatus={false} />
        )}
        <AlertDeliverySettings kind="weekly" value={deliveries.weekly} onChange={(value) => setDeliveries((d) => ({ ...d, weekly: value }))}
          canManage={canManage} canListGroups={hasPermission(user, "whatsapp:manage")} canSeeStatus={false}>
          {deliveries.weekly.delivery !== "none" && (
            <div className="rounded-lg border border-slate-200 p-3 space-y-3" data-testid="weekly-schedule">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <Label htmlFor="weekly-day" className="block text-xs text-slate-500">Jour d'envoi</Label>
                  <select id="weekly-day" value={weeklySchedule.day} disabled={!canManage} onChange={(e) => setWeeklySchedule((w) => ({ ...w, day: e.target.value }))}
                    className="mt-1 h-10 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="weekly-day">
                    {WEEKLY_DAYS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="weekly-hour" className="block text-xs text-slate-500">Heure (fuseau de l'entreprise)</Label>
                  <select id="weekly-hour" value={weeklySchedule.hour} disabled={!canManage} onChange={(e) => setWeeklySchedule((w) => ({ ...w, hour: Number(e.target.value) }))}
                    className="mt-1 h-10 rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="weekly-hour">
                    {Array.from({ length: 24 }, (_, hour) => <option key={hour} value={hour}>{String(hour).padStart(2, "0")} h</option>)}
                  </select>
                </div>
                {canManage && <WeeklySummaryPreview />}
              </div>
              <p className="text-xs text-slate-500">Le Copilote rédige chaque résumé ; s'il est indisponible ou si son plafond est atteint, un texte factuel est envoyé à la place.</p>
            </div>
          )}
        </AlertDeliverySettings>
      </div>

      <DeliveryJournal labelOf={(setting) => EVENT_ROWS.find((row) => row.key === setting)?.label} />

      <SaveBar dirty={dirty} saving={saving} canManage={canManage} saveDisabled={ALERT_DELIVERY_KINDS.some((kind) => deliveryMissingGroup(deliveries[kind]))} onSave={save} onRevert={revert} />
    </div>
  );
}

// --- Système (lecture seule) ------------------------------------------------

function formatSystemCheckedAt(iso) {
  if (!iso) return "Jamais";
  return new Date(iso).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function SettingsSystem() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/system");
      setStatus(data);
      setLoadFailed(false);
    } catch (e) {
      toast.error(errorMessage(e));
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loadFailed && !status) return <LoadError onRetry={load} />;
  if (status == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  const whatsapp = whatsappStatusLabel(status.whatsapp);

  return (
    <div className="space-y-4" data-testid="settings-system">
      <SectionHeader title="Système" description="Informations techniques de l'installation Jamora, en lecture seule." />

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
        <dl className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-xs text-slate-500 mb-1">Environnement</dt>
            <dd className="text-slate-900 font-medium truncate">{status.environment}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 mb-1">Version</dt>
            <dd className="text-slate-900 font-medium truncate" data-testid="system-version">{status.app_version || "dev"}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 mb-1">Commit</dt>
            <dd className="text-slate-900 font-medium truncate font-mono">{status.git_sha}</dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 mb-1">Backend</dt>
            <dd className="flex items-center gap-2 text-slate-900 font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
              Opérationnel
            </dd>
          </div>
          <div>
            <dt className="text-xs text-slate-500 mb-1">MongoDB</dt>
            <dd className="flex items-center gap-2 text-slate-900 font-medium">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${status.mongo_ok ? "bg-emerald-500" : "bg-red-500"}`} />
              {status.mongo_ok ? "Opérationnel" : "Indisponible"}
            </dd>
          </div>
        </dl>

        <div className="border-t border-slate-100 pt-4">
          <div className="text-xs text-slate-500 mb-2">Dernière vérification : {formatSystemCheckedAt(status.checked_at)}</div>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="btn-system-refresh">
            <RefreshCw size={13} className="mr-1.5" />
            Rafraîchir
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
        <div className="font-display font-semibold text-sm text-slate-900">File d'attente des notifications</div>
        <div className="grid grid-cols-4 gap-3">
          {Object.entries(QUEUE_STATUS_LABELS).map(([key, label]) => (
            <div key={key} className="text-center">
              <div className="text-lg font-semibold text-slate-900">{status.notifications_queue.counts?.[key] ?? 0}</div>
              <div className="text-xs text-slate-500">{label}</div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-3 text-sm">
          <span className="text-slate-500">Canal WhatsApp</span>
          <span className="flex items-center gap-2 text-slate-900 font-medium">
            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${whatsapp.dot}`} />
            {whatsapp.label}
            <Link to="/settings/whatsapp" className="text-jamora hover:underline text-xs font-normal">Voir le détail</Link>
          </span>
        </div>
      </div>
    </div>
  );
}
